import { PublicBusiness } from './businesses';
import { DateTimeISO, Money, PaginatedResponse } from './common';
import { ExternalBankAccountStatus } from './externalAccounts';
import { PublicUser } from './user';

export enum BankApplicationStatus {
  DRAFT = 0,
  AWAITING_E_SIGN = 1,
  SUBMITTED_BY_TEAMMATE = 2,
  SUBMITTED_BY_STAFF = 3,
  NEED_REVIEW = 4,
  COMPLETED = 5,
  DECLINED = 6,
  SUBMITTED = 7,
  EXTERNAL_ACCOUNT_REVIEW = 8,
}

export const BANK_APPLICATION_STATUS_TYPE = {
  [BankApplicationStatus.DRAFT]: 'DRAFTS',
  [BankApplicationStatus.AWAITING_E_SIGN]: 'AWAITING',
  [BankApplicationStatus.NEED_REVIEW]: 'UNDER_REVIEW',
  [BankApplicationStatus.COMPLETED]: 'COMPLETED',
  [BankApplicationStatus.DECLINED]: 'DECLINED',
  [BankApplicationStatus.SUBMITTED_BY_STAFF]: 'SUBMITTED_BY_STAFF',
  [BankApplicationStatus.SUBMITTED_BY_TEAMMATE]: 'SUBMITTED_BY_TEAMMATE',
  [BankApplicationStatus.SUBMITTED]: 'SUBMITTED',
};

export const BANK_APPLICATION_STATUS = {
  DRAFTS: 'DRAFTS',
  AWAITING: 'AWAITING',
  UNDER_REVIEW: 'UNDER_REVIEW',
  COMPLETED: 'COMPLETED',
  DECLINED: 'DECLINED',
  SUBMITTED_BY_STAFF: 'SUBMITTED_BY_STAFF',
  SUBMITTED_BY_TEAMMATE: 'SUBMITTED_BY_TEAMMATE',
  SUBMITTED: 'SUBMITTED',
};

export enum BankApplicationThirdPartyProcessorStatus {
  UNSUBMITTED = 0,
  SUBMITTED = 1,
  APPROVED = 2,
  REJECTED = 3,
  MANUAL_REVIEW = 4,
}

export enum BankApplicationAccountType {
  INDIVIDUAL = 0,
  JOINT = 1,
  BUSINESS = 2,
}

export enum BankApplicationSortBy {
  CREATED_ASCENDING = 'created ascending',
  CREATED_DESCENDING = 'created descending',
  APPLICATION_ID_ASCENDING = 'application id ascending',
  APPLICATION_ID_DESCENDING = 'application id descending',
  ACCOUNT_TYPE_ASCENDING = 'account type ascending',
  ACCOUNT_TYPE_DESCENDING = 'account type descending',
  LAST_UPDATED_ASCENDING = 'last updated ascending',
  LAST_UPDATED_DESCENDING = 'last updated descending',
  COMPANY_ASCENDING = 'company ascending',
  COMPANY_DESCENDING = 'company descending',
}

export enum SubmissionType {
  CUSTOMER_PORTAL = 0,
  STAFF_PORTAL = 1,
}

export enum ConsentType {
  WET_SIGNATURE = 'Wet Signature',
  ELECTRONIC_SIGNATURE = 'Electronic Signature',
}

export enum UserType {
  STAFF = 'staff',
  TEAMMATE = 'teammate',
  CUSTOMER = 'customer',
}

export type ConsumerInfo = {
  uuid: string;
  userId: string;
  companyId: string;
  companyName: string;
  firstName: string;
  lastName: string;
  fullName: string;
  cellPhoneNumber: string;
  email: string;
  extId: string;
  status: number;
  advisors: Array<{
    uuid: string;
  }>;
  adminUrl: string;
};

export type BankApplicationOwner = {
  consumer: {
    email: string;
    fullName: string;
    firstName: string;
    lastName: string;
    uuid: string;
  };
  isPrimaryOwner: boolean;
  requestDebitCard: boolean;
  sentSignatureRequestAt: DateTimeISO | null;
  signatureRequestFirstViewedAt: DateTimeISO | null;
  signedAt: string | null;
};

export type BankApplicationBusiness = {
  uuid: string;
  name: string;
};

export type BankApplicationAdvisor = {
  uuid: string;
  email: string;
  firstName: string;
  lastName: string;
  company: {
    uuid: string;
    name: string;
    companyLoginUrl: string;
    brandingTheme: {
      uuid: string;
      name: string;
      logoUrl: string;
      treasuryPrimeLogoId: string;
      primaryColor: string;
      buttonFontColor: string;
    };
    rateSavingInterest: number;
    debitCardProgramId: string;
    feeIncomingDomestic: Money;
    feeOutcomeDomestic: Money;
    feeMonthlySavingAccount: Money;
  };
  active: boolean;
};

export type BankApplicationWithdrawFrom = {
  uuid: string;
  description: string;
  status: ExternalBankAccountStatus;
};

export type BankApplicationListItem = {
  uuid: string;
  primaryOwner?: ConsumerInfo;
  status: BankApplicationStatus;
  business: BankApplicationBusiness | null;
  applicationId: string;
  accountType: BankApplicationAccountType;
  advisor: BankApplicationAdvisor;
  updatedAt: DateTimeISO;
  thirdPartyProcessorStatus: BankApplicationThirdPartyProcessorStatus;
};

export type Beneficiary = {
  firstName: string;
  lastName: string;
  tin: string;
  percentOfAllocation: string | number;
};

export type BankApplicationDocument = {
  url: string;
  name: string;
  side: 'front' | 'back';
  uploadedAt: DateTimeISO;
  uploadedBy: PublicUser;
};

export enum BankAccountApplicationRequirementType {
  person_application_requirement = 'Person Application Requirement',
  kyc_evaluation_requirement = 'KYC Evaluation Requirement',
  submit_to_third_party_requirement = 'Submit to Third Party Requirement',
  digital_signature_requirement = 'Digital Signature Requirement',
}

export enum RequirementStatus {
  PENDING = 'pending',
  STARTED = 'started',
  COMPLETED = 'completed',
}

export enum RequirementResult {
  SUCCESS = 'success',
  ERROR = 'error',
}

export enum KycEvaluationOutcome {
  APPROVED = 'Approved',
  MANUAL_REVIEW = 'Manual Review',
  DENIED = 'Denied',
  NEED_INVESTIGATION = 'Need Investigation',
}

export type BankApplicationRequirements = {
  uuid: string;
  priority: number;
  type: BankAccountApplicationRequirementType;
  status: RequirementStatus;
  result?: RequirementResult;
  errorMessage?: string;
  generatedAt: DateTimeISO;
  startedAt?: DateTimeISO;
  completedAt?: DateTimeISO;
  failedAt?: DateTimeISO;
};

export type BankApplicationPendingActions = {
  businessId: string;
  ownerId: string;
  actions: string[];
};

export type KYCEvaluation = {
  consumer?: PublicUser;
  consumerId?: string;
  business?: PublicBusiness;
  businessId?: string;
  status?: string;
  outcome?: KycEvaluationOutcome;
  score?: number;
  createdByThirdPartyAt?: DateTimeISO;
  kycEvaluationLink?: string;
};

export type BankApplicationDetails = {
  uuid: string;
  allOwnersViewedSignatureRequests: boolean;
  primaryOwner?: BankApplicationOwner;
  owners: BankApplicationOwner[];
  status: BankApplicationStatus;
  business: BankApplicationBusiness | null;
  applicationId: string;
  accountType: BankApplicationAccountType;
  advisor: BankApplicationAdvisor;
  updatedAt: DateTimeISO;
  withdrawFrom?: BankApplicationWithdrawFrom;
  depositAmount: Money;
  isReviewed: boolean;
  isVerifiedByStaff: boolean;
  isVerifiedByTeammate: boolean;
  createdBy?: {
    uuid: string;
    firstName: string;
    lastName: string;
    fullName: string;
    userType: UserType;
  };
  createdAt: DateTimeISO;
  submissionType: SubmissionType;
  submittedAt?: DateTimeISO;
  submittedBy?: BankApplicationAdvisor;
  deletedAt?: DateTimeISO;
  deletedBy?: {
    uuid: string;
    firstName: string;
    lastName: string;
    fullName: string;
    userType: UserType;
  };
  beneficiaries: Beneficiary[];
  documents: BankApplicationDocument[];
  thirdPartyProcessorStatus: BankApplicationThirdPartyProcessorStatus;
  requirements: BankApplicationRequirements[];
  pendingActions: BankApplicationPendingActions[];
  externalAccounts: {
    uuid: string;
    status: ExternalBankAccountStatus;
    description: string;
  }[];
  kycEvaluations: KYCEvaluation[];
};

export type BankApplicationListResponse = {
  bankApplications: PaginatedResponse<BankApplicationListItem>;
  aggregateInfo: BankApplicationAggregateInfo;
};

export type BankApplicationAggregateInfo = {
  totalCompleted: number;
  totalApproved: number;
  totalAwaitingESign: number;
  totalDraft: number;
  totalSubmitted: number;
  totalExternalAccountReview: number;
  totalUnderReview: number;
  totalDeclined: number;
};

export type BankApplicationListQueryParams = {
  statuses?: string;
  consumerId?: string;
  accountTypes?: string;
  accountStatuses?: string;
  teammateId?: string;
  companyId?: string;
  page?: number;
  recordsPerPage?: number;
  search?: string;
  sortBy?: BankApplicationSortBy;
};

export type BankApplicationFormValues = {
  applicationId: string;
  accountType: BankApplicationAccountType;
  companyId: string;
  advisorId: string;
  individualOwnerId: string;
  jointOwnerIds: string[];
  businessId: string;
  primaryOwner: string;
  beneficiaries: Beneficiary[];
  externalAccountDepositId: string;
  depositAmount: string;
  verifiedExternalAccount: boolean;
  verified: boolean;
};

export type PendingActionsFormFields = {
  id: string;
  businessId?: string;
  businessActions: { value: string }[];
  ownerId?: string;
  ownerActions: { value: string }[];
};

export const queryKeys = {
  list: ['bankApplicationsList'] as const,
  details: ['bankApplicationsDetails'] as const,
};
